import { useQuery } from '@tanstack/react-query';
import {
  BUSINESS_ADDRESS,
  BUSINESS_EMAIL,
  BUSINESS_MAP_EMBED_URL,
  BUSINESS_PHONE,
  BUSINESS_SOCIAL,
  BUSINESS_WHATSAPP_NUMBER,
} from '@sbg/shared/constants';
import type { PublicSettingsWithHome } from '@sbg/shared/schemas';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/keys';

/**
 * What the site shows before (or without) the API: the owner's business details from `@sbg/shared`
 * (D-315, D-318) — no component hard-codes them. They are named one by one, not read from
 * `DEFAULT_SETTINGS`: that object and the setting keys would otherwise ride along in the first page's
 * JavaScript. There are no opening hours: that row is simply not shown.
 */
export const FALLBACK_PUBLIC_SETTINGS: PublicSettingsWithHome = {
  whatsappNumber: BUSINESS_WHATSAPP_NUMBER,
  phone: BUSINESS_PHONE,
  email: BUSINESS_EMAIL,
  address: BUSINESS_ADDRESS,
  hours: null,
  mapEmbedUrl: BUSINESS_MAP_EMBED_URL,
  social: { ...BUSINESS_SOCIAL },
  featuredProductIds: [],
  flags: { newsletter: true },
  homeVideoUrl: null,
  // a site is in testing until a Super Admin's go-live confirmation says otherwise (D-382)
  siteLive: false,
};

/** Contact details, WhatsApp number, socials and feature flags (`GET /site/settings/public`). */
export function usePublicSettings(): PublicSettingsWithHome {
  const { data } = useQuery({
    queryKey: queryKeys.site.settings(),
    queryFn: ({ signal }) => apiFetch<PublicSettingsWithHome>('/site/settings/public', { signal }),
    staleTime: 10 * 60_000,
  });
  return data ?? FALLBACK_PUBLIC_SETTINGS;
}
